import React, { useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import KPI from './KPI';
import RankedKPI from './RankedKPI';
import Table from './Table';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const truncate = (str, max = 20) =>
  String(str || '').length > max
    ? String(str || '').substring(0, max) + '…'
    : String(str || '');

const ItemsChartsSection = ({ data = [], totalOrders = 0, isDark = false, }) => {
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');

  const handleLimitChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setLimit(val);
  };

  const displayedData = data.slice(0, limit).map(item => ({
    ...item,
    name: truncate(item.name)
  }));

  const backendTotal = Number(data[0]?.total_items_sold);
  const totalItems = Number.isFinite(backendTotal)
    ? backendTotal
    : data.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0);
  const avgItemsPerTicket = Number(totalOrders) > 0 ? totalItems / Number(totalOrders) : null;

  const mostOrderedItem = data.reduce(
    (max, item) =>
      Number(item.total_quantity || 0) > Number(max.total_quantity || 0) ? item : max,
    { name: '', total_quantity: 0 }
  );

  const minQty = data.length
    ? Math.min(...data.map(item => Number(item.total_quantity || 0)))
    : null;

  const leastOrderedItems = data.filter(
    item => minQty != null && Number(item.total_quantity || 0) === minQty
  );

  // Columns for Item Report Table
  const itemColumns = [
    {
      field: 'name',
      headerName: 'Item Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'total_quantity',
      headerName: 'Total Quantity',
      flex: 1,
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',

      renderHeader: () => (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span className="cursor-help">
                Total Quantity ⓘ
              </span>
            </Tooltip.Trigger>

            <Tooltip.Portal>
              <Tooltip.Content
                className="bg-black text-white text-xs rounded px-2 py-1 shadow-lg z-[9999]"
                sideOffset={5}
              >
                Total number of units ordered

                <Tooltip.Arrow className="fill-black" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ),
    },
    {
      field: 'distinct_orders',
      headerName: 'Orders Count',
      flex: 1,
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',

      renderHeader: () => (
        <Tooltip.Provider delayDuration={200}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span className="cursor-help">
                Orders Count ⓘ
              </span>
            </Tooltip.Trigger>

            <Tooltip.Portal>
              <Tooltip.Content
                className="bg-black text-white text-xs rounded px-2 py-1 shadow-lg z-[9999]"
                sideOffset={5}
              >
                Number of unique orders the item appeared in

                <Tooltip.Arrow className="fill-black" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ),
    },
    {
      field: 'average_bump_time_seconds',
      headerName: 'Avg. Bump Time',
      flex: 1,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',

      // Convert seconds → minutes
      valueGetter: (value) => {
        if (value == null || isNaN(Number(value))) {
          return null;
        }
        return Number(value) / 60;
      },

      // What the user actually sees
      renderCell: (params) => {
        return params.value == null
          ? '—'
          : `${Number(params.value).toFixed(1)} min`;
      },
    },
  ];

  return (
    <div className="p-4 dark:text-gray-100 mb-10 space-y-6">
      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPI
          title="Total Items Ordered"
          value={totalItems.toLocaleString() ?? '-'}
          color="blue"
        />
        <KPI
          title="Avg. Items Per Ticket"
          value={avgItemsPerTicket == null ? '—' : avgItemsPerTicket.toFixed(1)}
          color="purple" />
        <RankedKPI
          title="Most Ordered Item"
          label={mostOrderedItem.name || '—'}
          value={Number(mostOrderedItem.total_quantity || 0).toLocaleString()}
          color="green"
        />
        <RankedKPI
          title="Least Ordered Item(s)"
          label={
            leastOrderedItems.length === 1
              ? leastOrderedItems[0].name
              : leastOrderedItems.length > 1
                ? `${leastOrderedItems.length} items`
                : '—'
          }
          value={minQty == null ? '—' : minQty}
          color="yellow"
          showTooltip={leastOrderedItems.length > 1}
          tooltipContent={
            <>
              {leastOrderedItems.map((item, index) => (
                <div key={index}>{item.name}</div>
              ))}
            </>
          }
        />
      </div>
      {/* Item Report Table*/}
      <Table
        title="Item Report"
        rows={data}
        columns={itemColumns}
        isDark={isDark}
        getRowId={(row) => row.name}
        height={500}

        searchable
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by Item..."

        exportable
        exportTitle="Item Report"
        exportFileName="Item Report"
      />
      {/* Header & Filter */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-blue-800 dark:text-blue-300 font-semibold text-lg">MOST COMMON ITEMS</h3>
          <Tooltip.Provider delayDuration={300}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <span className="text-gray-400 cursor-pointer text-sm">ⓘ</span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  className="bg-black text-white text-xs rounded px-3 py-2 shadow-md z-50 max-w-[220px]"
                  sideOffset={5}
                >
                  <div><strong>Total Quantity:</strong> Total number of individual units ordered (e.g., 3x Fries = 3).</div>
                  <div className="mt-1"><strong>Orders Count:</strong> Number of unique orders that included the item.</div>
                  <Tooltip.Arrow className="fill-black" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        <select
          value={limit}
          onChange={handleLimitChange}
          className="border rounded px-2 py-1 text-sm bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        >
          <option value={10}>Top 10</option>
          <option value={20}>Top 20</option>
          <option value={data.length}>Show All</option>
        </select>
      </div>

      {/* Chart */}
      {displayedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={450}>
          <BarChart
            data={displayedData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 100, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis
              dataKey="name"
              type="category"
              width={200}
              tick={{ fontSize: 13, fontWeight: 500 }}
            />
            <RechartsTooltip />
            <Legend verticalAlign="top" height={36} />
            <Bar dataKey="total_quantity" fill="#4A90E2" name="Total Quantity" />
            <Bar dataKey="distinct_orders" fill="#82ca9d" name="Orders Count" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">No item data available.</p>
      )}


    </div>
  );
};

export default ItemsChartsSection;
